/**
 * PayoutHistoryList Component
 *
 * List of past payout requests.
 */

import React from 'react';
import type { PayoutRequest } from '../hooks';

interface PayoutHistoryListProps {
  payouts: PayoutRequest[];
  isLoading: boolean;
}

const STATUS_CONFIG = {
  requested: { label: 'Requested', bg: 'bg-amber-100', color: 'text-amber-700' },
  processing: { label: 'Processing', bg: 'bg-blue-100', color: 'text-blue-700' },
  completed: { label: 'Completed', bg: 'bg-green-100', color: 'text-green-700' },
  failed: { label: 'Failed', bg: 'bg-red-100', color: 'text-red-700' },
};

export const PayoutHistoryList: React.FC<PayoutHistoryListProps> = ({
  payouts,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="flex justify-between">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
              <div className="h-6 bg-gray-200 rounded w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (payouts.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <HistoryIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No payout history</h3>
        <p className="text-gray-500">
          Your payout requests will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {payouts.map((payout) => {
        const status = STATUS_CONFIG[payout.status];

        return (
          <div
            key={payout.id}
            className="bg-white rounded-xl border border-gray-200 p-4"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 text-lg">
                    ${payout.netAmount.toFixed(2)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>
                </div>

                <div className="text-sm text-gray-500 space-y-1">
                  <p>
                    <span className="text-gray-400">To:</span> {payout.payoutMethodName}
                  </p>
                  <p>
                    <span className="text-gray-400">Requested:</span>{' '}
                    {payout.requestedAt.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                  {payout.completedAt && (
                    <p>
                      <span className="text-gray-400">Completed:</span>{' '}
                      {payout.completedAt.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </p>
                  )}
                  {payout.transactionReference && (
                    <p>
                      <span className="text-gray-400">Ref:</span>{' '}
                      <span className="font-mono text-xs">{payout.transactionReference}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Transfer Fee Info */}
              {payout.transferFee > 0 && (
                <div className="text-right text-sm">
                  <p className="text-gray-500">
                    Amount: ${payout.amount.toFixed(2)}
                  </p>
                  <p className="text-red-500">
                    Fee: -${payout.transferFee.toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {/* Status Timeline for non-completed */}
            {payout.status !== 'completed' && payout.status !== 'failed' && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-gray-600">Requested</span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                  <div className="flex items-center gap-1">
                    <div className={`w-2 h-2 rounded-full ${
                      payout.status === 'processing' ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'
                    }`} />
                    <span className={payout.status === 'processing' ? 'text-blue-600' : 'text-gray-400'}>
                      Processing
                    </span>
                  </div>
                  <div className="flex-1 h-px bg-gray-200" />
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-gray-300" />
                    <span className="text-gray-400">Completed</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Icons
const HistoryIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default PayoutHistoryList;
